/**
 * EVC Swap Debt Dialog
 *
 * Uses Euler swap API to swap debt from one token to another:
 * 1. Borrow new debt token to swapper
 * 2. Swap new token to old debt token via Euler's swap routing
 * 3. Repay old debt with swapped tokens
 *
 * All operations happen in a single EVC batch transaction.
 *
 * Includes an inline vault selector (same pattern as AddToPosition dialog)
 * to let the user pick the target debt vault from the same market.
 */

'use client'

import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ConnectCTAPanel } from '@/ui/organisms/connect-cta-panel/ConnectCTAPanel'
import { usePrivy } from '@privy-io/react-auth'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { Loader2 } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type Address, formatUnits, parseUnits, zeroAddress } from 'viem'
import { useAccount, useConfig, useReadContracts, useWriteContract } from 'wagmi'

import { formatPercentage } from '@/domain/common/format'
import { decodeEulerCapToBigInt } from '@/domain/euler/shared/caps'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import {
  type EulerSwapResponse,
  SwapperMode,
  hasSwapAndVerify,
  useEulerSwapQuoteExtended,
  useSimulatedBestQuote,
} from '@/domain/euler/swap'
import { getSubAccount } from '@/domain/evc'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SwapErrorDisplay } from '@/features/actions/components/SwapErrorDisplay'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { isTimeoutError, useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { type ELendVaultData, E_LEND_CHAIN_ID, useELend } from '@/features/elend/logic/useELend'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { isUserRejection } from '@/lib/errors'
import { getTokenImage } from '@/ui/assets/tokens'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { BorrowCapDetail } from '@/ui/molecules/borrow-cap-detail/BorrowCapDetail'
import { SlippagePanel } from '@/ui/molecules/slippage-panel/SlippagePanel'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import {
  RepayType,
  type SwapMulticallTransactionItem,
  type SwapVerifierDebtMaxTransaction,
  type TransactionItems,
  TransactionLabel,
  TransactionSubLabel,
  type VaultSwapDebtTransactionItem,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'
import { ChevronDown, Settings } from 'lucide-react'
import { SwapDebtSuccessView } from './components/SwapDebtSuccessView'

// =============================================================================
// Types
// =============================================================================

interface EvcSwapDebtDialogContentProps {
  closeDialog: () => void
  fromVault: Address
  fromSymbol: string
  fromDecimals: number
  fromAsset: Address
  fromLogoURI?: string
  currentDebt: string
  subAccountId?: number
  collateralVaults?: Address[]
}

// =============================================================================
// Transaction Builder
// =============================================================================

/**
 * Builds a VaultSwapDebtTransactionItem compound transaction matching the
 * Euler monorepo's `swapDebtAssets` selector (source of truth).
 *
 * Structure: VAULT_SWAP_DEBT compound → [BORROW(+ENABLE_CONTROLLER), SWAP, VERIFY, DISABLE_CONTROLLER]
 */
function buildSwapDebtTransactions({
  fromVault,
  fromDecimals,
  fromAsset,
  fromSymbol,
  toVault,
  toDecimals,
  toAsset,
  toSymbol,
  address,
  subAccountId = 1,
  eulerSwapQuote,
}: {
  fromVault: Address
  fromDecimals: number
  fromAsset: Address
  fromSymbol: string
  toVault: Address
  toDecimals: number
  toAsset: Address
  toSymbol: string
  address: Address
  subAccountId?: number
  eulerSwapQuote?: EulerSwapResponse | null
}): TransactionItems {
  if (!address || address === zeroAddress) {
    return []
  }

  if (!hasSwapAndVerify(eulerSwapQuote)) {
    return []
  }

  const swap = eulerSwapQuote.swap
  const verify = eulerSwapQuote.verify
  const borrowValue = eulerSwapQuote.amountInMax || '0'

  const position = getSubAccount(address, subAccountId)

  const fromAssetInfo = {
    address: fromAsset,
    name: fromSymbol,
    symbol: fromSymbol,
    decimals: fromDecimals,
  }

  const toAssetInfo = {
    address: toAsset,
    name: toSymbol,
    symbol: toSymbol,
    decimals: toDecimals,
  }

  const enableController = {
    label: TransactionLabel.ENABLE_CONTROLLER,
    to: position,
    from: position,
    asset: toAssetInfo,
    vault: toVault,
  } as const

  const disableController = {
    label: TransactionLabel.DISABLE_CONTROLLER,
    to: position,
    from: position,
    asset: fromAssetInfo,
    vault: fromVault,
    value: '0',
  } as const

  const swapMulticall: SwapMulticallTransactionItem = {
    label: TransactionLabel.SWAP_MULTICALL,
    from: position,
    to: position,
    asset: toAssetInfo,
    fromVault: toVault,
    toVault: fromVault,
    accountTo: position,
    receiver: fromVault,
    assetIn: toAssetInfo,
    assetOut: fromAssetInfo,
    amountIn: eulerSwapQuote.amountIn,
    amountOut: eulerSwapQuote.amountOut,
    swapperAddress: swap.swapperAddress,
    swapperData: swap.swapperData,
    calls: swap.multicallItems.map((item) => item.data),
  }

  const swapVerify: SwapVerifierDebtMaxTransaction = {
    label: TransactionLabel.SWAP_VERIFIER_DEBT_MAX,
    from: position,
    to: position,
    asset: fromAssetInfo,
    vault: verify.vault,
    account: verify.account,
    amount: verify.amount,
    deadline: verify.deadline.toString(),
    verifierAddress: verify.verifierAddress,
    verifierData: verify.verifierData,
  }

  const compoundTransaction: VaultSwapDebtTransactionItem = {
    label: TransactionLabel.VAULT_SWAP_DEBT,
    subLabel: TransactionSubLabel.REPAY_WITH_SHARES_SWAP_EXACT_OUT,
    from: position,
    to: position,
    fromVault,
    toVault,
    isMax: true,
    type: RepayType.COLLATERAL,
    fromAmount: borrowValue,
    amountIn: address,
    transactions: [
      {
        from: position,
        to: swap.swapperAddress,
        vault: toVault,
        label: TransactionLabel.BORROW,
        asset: toAssetInfo,
        value: borrowValue,
        isMax: false,
        transactions: [enableController],
      },
      swapMulticall,
      swapVerify,
      disableController,
    ],
  }

  return [compoundTransaction]
}

/**
 * Upstream-equivalent: min(cash, max(0, borrowCap - totalBorrowed)),
 * or just cash when there's no borrow cap.
 */
function selectAvailableToBorrow(vault: ELendVaultData, borrowCapRaw: bigint | undefined): NormalizedUnitNumber {
  const cash = NormalizedUnitNumber(vault.totalAssets.minus(vault.totalBorrowed).toFixed())

  if (!borrowCapRaw) {
    return cash.lte(0) ? NormalizedUnitNumber(0) : cash
  }

  const assetDecimals = vault.assetDecimals
  const borrowCap = NormalizedUnitNumber(formatUnits(borrowCapRaw, assetDecimals))
  const remainingCap = NormalizedUnitNumber(borrowCap.minus(vault.totalBorrowed).toFixed())

  if (remainingCap.lte(0)) return NormalizedUnitNumber(0)

  return cash.lt(remainingCap) ? (cash.lte(0) ? NormalizedUnitNumber(0) : cash) : remainingCap
}

// =============================================================================
// Main Component
// =============================================================================

export function EvcSwapDebtDialogContent({
  closeDialog,
  fromVault,
  fromSymbol,
  fromDecimals,
  fromAsset,
  fromLogoURI,
  currentDebt,
  subAccountId = 1,
  collateralVaults = [],
}: EvcSwapDebtDialogContentProps) {
  const { address } = useAccount()
  const { connectWallet } = usePrivy()
  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Vault selector: fetch all vaults, filter to same market + different asset
  const { vaults: elendVaults, isLoading: isVaultsLoading } = useELend(address)

  const fromMarketKey = useMemo(() => {
    const sourceVault = elendVaults.find((v) => v.address.toLowerCase() === fromVault.toLowerCase())
    if (sourceVault?.market?.key) return sourceVault.market.key
    return getVaultMarket(fromVault)?.key
  }, [elendVaults, fromVault])

  const sourceVaultData = useMemo(
    () => elendVaults.find((v) => v.address.toLowerCase() === fromVault.toLowerCase()),
    [elendVaults, fromVault],
  )

  const collateralVaultsLower = useMemo(() => collateralVaults.map((vault) => vault.toLowerCase()), [collateralVaults])

  const candidateVaults = useMemo(() => {
    if (!elendVaults.length || !fromMarketKey) return []
    return elendVaults.filter((v) => {
      if (v.address.toLowerCase() === fromVault.toLowerCase()) return false
      if (v.market?.key !== fromMarketKey) return false
      if (!v.underlyingAsset) return false
      if (v.underlyingAsset.toLowerCase() === fromAsset.toLowerCase()) return false
      return true
    })
  }, [elendVaults, fromMarketKey, fromVault, fromAsset])

  const { data: rawBatch1 } = useReadContracts({
    contracts: candidateVaults.flatMap((vault) => [
      { address: vault.address, abi: eVaultImplementationAbi, functionName: 'caps' as const, chainId: E_LEND_CHAIN_ID },
      {
        address: vault.address,
        abi: eVaultImplementationAbi,
        functionName: 'LTVList' as const,
        chainId: E_LEND_CHAIN_ID,
      },
    ]),
    query: { enabled: candidateVaults.length > 0 },
  })
  const batch1 = rawBatch1 as Array<{ result?: unknown }> | undefined

  const ltvListByCandidate = useMemo(() => {
    if (!batch1) return []
    return candidateVaults.map((_, i) => (batch1[i * 2 + 1]?.result as Address[] | undefined) ?? [])
  }, [candidateVaults, batch1])

  const ltvFullContracts = useMemo(() => {
    return candidateVaults.flatMap(
      (vault, i) =>
        ltvListByCandidate[i]?.map((collateral) => ({
          address: vault.address,
          abi: eVaultImplementationAbi,
          functionName: 'LTVFull' as const,
          args: [collateral] as const,
          chainId: E_LEND_CHAIN_ID,
        })) ?? [],
    )
  }, [candidateVaults, ltvListByCandidate])

  const { data: rawBatch2 } = useReadContracts({
    contracts: ltvFullContracts,
    query: { enabled: ltvFullContracts.length > 0 },
  })
  const batch2 = rawBatch2 as Array<{ result?: readonly [number, number, number, bigint, number] }> | undefined

  const borrowableVaults = useMemo(() => {
    const set = new Set<string>()
    if (!batch1 || !batch2) return set

    let ltvIdx = 0
    for (let i = 0; i < candidateVaults.length; i++) {
      const ltvList = ltvListByCandidate[i] ?? []
      for (let j = 0; j < ltvList.length; j++) {
        const collateral = ltvList[j]!
        const ltv = batch2[ltvIdx]?.result
        if (ltv && collateralVaultsLower.includes(collateral.toLowerCase())) {
          const [borrowLTV, liquidationLTV] = ltv
          if (borrowLTV > 0 && liquidationLTV > 0) {
            set.add(candidateVaults[i]!.address.toLowerCase())
          }
        }
        ltvIdx++
      }
    }
    return set
  }, [candidateVaults, ltvListByCandidate, batch1, batch2, collateralVaultsLower])

  const targetVaultOptions = useMemo(() => {
    if (!batch1) return []
    return candidateVaults
      .filter((v, i) => {
        if (!borrowableVaults.has(v.address.toLowerCase())) return false
        const capsResult = batch1[i * 2]?.result as [number, number] | undefined
        const borrowCapRaw = decodeEulerCapToBigInt(capsResult?.[1])
        const available = selectAvailableToBorrow(v, borrowCapRaw)
        return available.gt(0)
      })
      .sort((a, b) => {
        const aIsCollateral = collateralVaultsLower.includes(a.address.toLowerCase())
        const bIsCollateral = collateralVaultsLower.includes(b.address.toLowerCase())

        if (aIsCollateral !== bIsCollateral) {
          return aIsCollateral ? 1 : -1
        }

        return (a.borrowAPY ?? 0) - (b.borrowAPY ?? 0)
      })
  }, [candidateVaults, batch1, borrowableVaults, collateralVaultsLower])

  const selectableTargetVaultOptions = useMemo(
    () => targetVaultOptions.filter((v) => !collateralVaultsLower.includes(v.address.toLowerCase())),
    [targetVaultOptions, collateralVaultsLower],
  )

  const [selectedVaultAddress, setSelectedVaultAddress] = useState<string | null>(null)
  const [vaultSelectorOpen, setVaultSelectorOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const selectedVault: ELendVaultData | undefined = useMemo(() => {
    if (selectedVaultAddress) {
      const selected = targetVaultOptions.find((v) => v.address.toLowerCase() === selectedVaultAddress.toLowerCase())
      if (selected && !collateralVaultsLower.includes(selected.address.toLowerCase())) {
        return selected
      }
    }
    return selectableTargetVaultOptions[0]
  }, [targetVaultOptions, selectableTargetVaultOptions, selectedVaultAddress, collateralVaultsLower])

  const toVault = selectedVault?.address as Address | undefined
  const toSymbol = selectedVault?.assetSymbol ?? selectedVault?.symbol ?? ''
  const toDecimals = selectedVault?.assetDecimals ?? 18
  const toAsset = selectedVault?.underlyingAsset as Address | undefined

  const filteredTargetVaults = useMemo(() => {
    if (!searchQuery) return targetVaultOptions
    const q = searchQuery.toLowerCase()
    return targetVaultOptions.filter(
      (v) =>
        (v.assetSymbol ?? v.symbol).toLowerCase().includes(q) ||
        v.curatorName?.toLowerCase().includes(q) ||
        v.market?.name?.toLowerCase().includes(q),
    )
  }, [targetVaultOptions, searchQuery])
  const [slippage, setSlippage] = useState<Percentage>(Percentage(0.005))

  const position = useMemo(() => {
    if (!address || address === zeroAddress) return zeroAddress
    return getSubAccount(address, subAccountId)
  }, [address, subAccountId])

  const currentDebtBigInt = useMemo(() => {
    const debt = currentDebt || '0'
    if (!debt || debt === '0') return 0n
    return parseUnits(debt, fromDecimals)
  }, [currentDebt, fromDecimals])

  const needsSwap = useMemo(
    () => Boolean(fromAsset && toAsset && fromAsset.toLowerCase() !== toAsset.toLowerCase()),
    [fromAsset, toAsset],
  )

  const swapQueryEnabled = Boolean(
    needsSwap && toVault && address && address !== zeroAddress && position !== zeroAddress && currentDebtBigInt > 0n,
  )

  const {
    quote: eulerSwapQuote,
    quotes: allQuotes,
    isLoading: isEulerQuoteLoading,
    isRefetching: isQuoteRefetching,
    error: eulerQuoteError,
    refetch: refetchQuotes,
  } = useEulerSwapQuoteExtended({
    tokenIn: needsSwap ? toAsset : undefined,
    tokenOut: needsSwap ? fromAsset : undefined,
    amount: currentDebtBigInt.toString(),
    vaultIn: needsSwap ? toVault : undefined,
    receiver: needsSwap ? fromVault : undefined,
    accountIn: needsSwap ? position : undefined,
    accountOut: needsSwap ? position : undefined,
    origin: address,
    slippage: Number(slippage.toFixed(4)) * 100,
    enabled: swapQueryEnabled,
    swapperMode: SwapperMode.TARGET_DEBT,
    targetDebt: '0',
    currentDebt: currentDebtBigInt.toString(),
    isRepay: true,
    dustAccount: address,
  })

  const buildTransactionsForQuote = useCallback(
    (quote: EulerSwapResponse): TransactionItems => {
      if (!address || address === zeroAddress) return []
      if (!fromVault || !fromAsset || !toVault || !toAsset) return []

      return buildSwapDebtTransactions({
        fromVault,
        fromDecimals,
        fromAsset,
        fromSymbol,
        toVault,
        toDecimals,
        toAsset,
        toSymbol,
        address,
        subAccountId,
        eulerSwapQuote: quote,
      })
    },
    [address, fromVault, fromDecimals, fromAsset, fromSymbol, toVault, toDecimals, toAsset, toSymbol, subAccountId],
  )

  const {
    quote: validatedQuote,
    transactions: validatedTransactions,
    isSimulating: isValidatingQuotes,
    error: validationError,
    refetch: refetchSimulation,
  } = useSimulatedBestQuote({
    quotes: allQuotes,
    buildTransactions: buildTransactionsForQuote,
    queryKeyDeps: [position, fromVault, toVault, currentDebtBigInt.toString()],
    enabled: allQuotes.length > 0 && !!address && address !== zeroAddress,
  })

  const handleRefetchQuotes = useCallback(() => {
    refetchQuotes()
    refetchSimulation()
  }, [refetchQuotes, refetchSimulation])

  const displayQuote = validatedQuote ?? (validationError ? null : eulerSwapQuote)

  // =========================================================================
  // EVC simulation
  // =========================================================================
  const config = useConfig()
  const {
    data: simulationData,
    error: simulationError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions: validatedTransactions,
    query: {
      enabled: !!address && validatedTransactions.length > 0 && !!validatedQuote,
    },
  })

  // =========================================================================
  // Execution
  // =========================================================================
  const { writeContract, isPending: isWriting, error: writeError, data: txHash, reset: resetWrite } = useWriteContract()

  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })

  useEffect(() => {
    if (isConfirmed) {
      invalidateSubaccounts()
    }
  }, [isConfirmed, invalidateSubaccounts])

  const handleExecute = useCallback(async () => {
    if (!simulationData?.request) return
    resetWrite()
    try {
      writeContract(simulationData.request as Parameters<typeof writeContract>[0])
    } catch {
      // Error handled by writeError
    }
  }, [simulationData, writeContract, resetWrite])

  const swapTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isValidatingQuotes || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || validationError || simulationError || eulerQuoteError,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    swapTimeout.handleActionClick(handleExecute)
  }, [swapTimeout, handleExecute])

  // =========================================================================
  // Action status
  // =========================================================================
  const fromToken = useMemo(
    () =>
      new Token({
        address: CheckedAddress(fromAsset),
        symbol: TokenSymbol(fromSymbol),
        name: fromSymbol,
        decimals: fromDecimals,
        unitPriceUsd: String(sourceVaultData?.assetPrice ?? 0),
      }),
    [fromAsset, fromSymbol, fromDecimals, sourceVaultData?.assetPrice],
  )

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isWriting || isWaiting) return 'loading'
    if (isConfirmed) return 'success'

    const hasBlockingError =
      (simulationError && !isUserRejection(simulationError)) ||
      (writeError && !isUserRejection(writeError)) ||
      (waitError && !isUserRejection(waitError))

    if (hasBlockingError) return 'disabled'
    if (!needsSwap || !displayQuote) return 'disabled'

    if (!simulationData) {
      return isSimulating || isEulerQuoteLoading || isValidatingQuotes ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [
    simulationData,
    isSimulating,
    isWriting,
    isWaiting,
    isConfirmed,
    simulationError,
    writeError,
    waitError,
    needsSwap,
    displayQuote,
    isEulerQuoteLoading,
    isValidatingQuotes,
  ])

  const displayError = useMemo(() => {
    if (swapTimeout.isTimedOut) return swapTimeout.displayError
    const errors = [validationError, simulationError, writeError, waitError].filter(Boolean)
    return errors.find((err) => !isUserRejection(err as any)) || null
  }, [validationError, simulationError, writeError, waitError, swapTimeout.isTimedOut, swapTimeout.displayError])

  // =========================================================================
  // Render
  // =========================================================================

  if (!address) {
    const icons = assets.walletIcons
    const WALLET_ICONS_PATHS = [icons.metamask, icons.walletConnect, icons.coinbase]
    return (
      <MultiPanelDialog>
        <DialogPanel>
          <div className="flex items-center justify-center py-8">
            <ConnectCTAPanel
              header="Connect your wallet to swap debt"
              iconPaths={WALLET_ICONS_PATHS}
              action={connectWallet}
              buttonText="Connect wallet"
            />
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  if (isConfirmed) {
    const toToken = new Token({
      address: CheckedAddress(toAsset ?? zeroAddress),
      symbol: TokenSymbol(toSymbol),
      name: toSymbol,
      decimals: toDecimals,
      unitPriceUsd: String(selectedVault?.assetPrice ?? 0),
    })
    const repaidAmount = NormalizedUnitNumber(formatUnits(currentDebtBigInt, fromDecimals))
    const newDebtAmountValue = displayQuote
      ? NormalizedUnitNumber(formatUnits(BigInt(displayQuote.amountIn), toDecimals))
      : NormalizedUnitNumber(0)

    return (
      <MultiPanelDialog>
        <SwapDebtSuccessView
          fromToken={fromToken}
          fromAmount={repaidAmount}
          toToken={toToken}
          toAmount={newDebtAmountValue}
          onProceed={closeDialog}
        />
      </MultiPanelDialog>
    )
  }

  const isBatchLoading = candidateVaults.length > 0 && (!batch1 || (ltvFullContracts.length > 0 && !batch2))

  if (isVaultsLoading || isBatchLoading) {
    return (
      <MultiPanelDialog>
        <DialogTitle className="font-semibold text-white text-xl">Swap Debt</DialogTitle>
        <DialogPanel>
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  if (selectableTargetVaultOptions.length === 0) {
    return (
      <MultiPanelDialog>
        <DialogTitle className="font-semibold text-white text-xl">Swap Debt</DialogTitle>
        <DialogPanel>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">No compatible target vaults found in this market to swap debt to.</p>
            <Button onClick={closeDialog} variant="secondary" className="mt-4">
              Close
            </Button>
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  const fromImage = getTokenImage(TokenSymbol(fromSymbol)) || fromLogoURI
  const toImage = getTokenImage(TokenSymbol(toSymbol))
  const newDebtAmount = displayQuote ? Number(formatUnits(BigInt(displayQuote.amountIn), toDecimals)) : 0

  return (
    <MultiPanelDialog>
      <DialogTitle className="font-semibold text-white text-xl">Swap Debt</DialogTitle>
      <DialogPanel>
        {/* Summary */}
        <div className="flex flex-col gap-4">
          {/* Current Debt (From) */}
          <div className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-3">
              {fromImage && <Image src={fromImage} alt={fromSymbol} width={32} height={32} className="rounded-full" />}
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">Current Debt</span>
                <span className="font-medium text-sm text-white">{fromSymbol}</span>
              </div>
            </div>
            <span className="font-semibold text-lg text-white">
              {Number(currentDebt).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </span>
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5">
              <svg className="h-4 w-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>

          {/* New Debt (To) - entire card is clickable, opens vault selector dialog */}
          <div className="overflow-hidden rounded-xl border border-primary/10 bg-primary/[0.02]">
            <Dialog open={vaultSelectorOpen} onOpenChange={setVaultSelectorOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-between rounded-xl p-4 transition-colors hover:bg-primary/[0.04]"
                  disabled={isWriting || isWaiting}
                >
                  <div className="flex items-center gap-3">
                    {selectedVault ? (
                      <>
                        {toImage && (
                          <Image src={toImage} alt={toSymbol} width={32} height={32} className="rounded-full" />
                        )}
                        <div className="flex flex-col items-start">
                          <span className="text-muted-foreground text-xs">New Debt</span>
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-sm text-white">{toSymbol}</span>
                          </div>
                          {(selectedVault.curatorName || selectedVault.market) && (
                            <span className="text-muted-foreground text-xs">
                              {[
                                selectedVault.curatorName && `by ${selectedVault.curatorName}`,
                                selectedVault.market?.name,
                              ]
                                .filter(Boolean)
                                .join(' • ')}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-muted-foreground text-xs">New Debt</span>
                          <span className="text-muted-foreground text-sm">Select vault</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      {isEulerQuoteLoading || isValidatingQuotes ? (
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      ) : newDebtAmount > 0 ? (
                        <span className="font-semibold text-lg text-primary">
                          {newDebtAmount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 6,
                          })}
                        </span>
                      ) : selectedVault ? (
                        <span className="text-muted-foreground">—</span>
                      ) : null}
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
                  </div>
                </button>
              </DialogTrigger>

              <DialogContent
                className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background px-6 pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
                aria-describedby={undefined}
                contentVerticalPosition="top"
                overlayClassName="z-[240]"
                closeClassName="top-6"
              >
                <div className="mb-4">
                  <DialogTitle className="mb-2">
                    <Typography variant="p" className="font-bold text-lg" gradient>
                      Select vault
                    </Typography>
                  </DialogTitle>
                  <SearchInput
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search vaults..."
                    autoFocus
                  />
                </div>

                <div className="max-h-80 space-y-2 overflow-y-auto pb-4">
                  <Typography variant="p" className="pt-3 font-semibold">
                    Available Vaults
                  </Typography>

                  {filteredTargetVaults.length === 0 ? (
                    <div className="p-4 text-center">
                      <Typography variant="prompt">No vaults found</Typography>
                    </div>
                  ) : (
                    filteredTargetVaults.map((vault) => {
                      const vaultImage = getTokenImage(TokenSymbol(vault.assetSymbol ?? vault.symbol))
                      const vaultSymbol = vault.assetSymbol ?? vault.symbol
                      const isCollateralVault = collateralVaultsLower.includes(vault.address.toLowerCase())
                      return (
                        <button
                          key={vault.address}
                          type="button"
                          className={`flex w-full items-center rounded-sm p-3 text-left transition-colors ${
                            isCollateralVault ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted'
                          }`}
                          onClick={() => {
                            if (isCollateralVault) return
                            setSelectedVaultAddress(vault.address)
                            setVaultSelectorOpen(false)
                            setSearchQuery('')
                          }}
                          disabled={isCollateralVault}
                        >
                          <div className="flex w-full flex-col gap-1">
                            <div className="flex w-full flex-row items-center justify-between">
                              <div className="flex flex-row items-center gap-3">
                                <TokenIcon
                                  token={{ logoURI: vaultImage, symbol: TokenSymbol(vaultSymbol) }}
                                  className="h-6 w-6"
                                />
                                <div className="flex flex-col text-left">
                                  <Typography className="font-semibold">{vaultSymbol}</Typography>
                                  {(vault.curatorName || vault.market) && (
                                    <Typography variant="prompt">
                                      {[vault.curatorName && `by ${vault.curatorName}`, vault.market?.name]
                                        .filter(Boolean)
                                        .join(' • ')}
                                    </Typography>
                                  )}
                                </div>
                              </div>
                              <Typography variant="prompt" className="text-primary">
                                {(vault.borrowAPY ?? 0).toFixed(2)}% Borrow APY
                              </Typography>
                            </div>
                            {isCollateralVault && (
                              <Typography variant="prompt" className="pl-9 text-yellow-500/70">
                                Used as collateral in this position
                              </Typography>
                            )}
                          </div>
                        </button>
                      )
                    })
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          {selectedVault?.borrowAPY !== undefined && (
            <TransactionOverviewDetailsItem label="New Borrow APY">
              <span className="text-primary">{selectedVault.borrowAPY.toFixed(2)}%</span>
            </TransactionOverviewDetailsItem>
          )}
          <TransactionOverviewDetailsItem label="Slippage tolerance">
            <SlippagePanel
              slippage={slippage}
              setSlippage={setSlippage}
              title="Swap Slippage"
              trigger={
                <button
                  type="button"
                  className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-white"
                >
                  <span>{formatPercentage(slippage, { minimumFractionDigits: 1 })}</span>
                  <Settings className="h-3.5 w-3.5" />
                </button>
              }
            />
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Swap via">
            {isValidatingQuotes ? (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : displayQuote ? (
              <span className="text-green-400">
                EulerSwap
                {displayQuote.route?.length
                  ? ` (${displayQuote.route.map((r: any) => r.providerName).join(' → ')})`
                  : ''}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TransactionOverviewDetailsItem>
          {toVault && <BorrowCapDetail vaultAddress={toVault} decimals={toDecimals} symbol={toSymbol} />}
        </div>

        {/* Quote Loading */}
        {(isEulerQuoteLoading || isValidatingQuotes) && !displayQuote && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Fetching swap quote...
          </div>
        )}

        {/* Swap error (quote-specific) */}
        {!swapTimeout.isTimedOut && eulerQuoteError && !isUserRejection(eulerQuoteError) && (
          <SwapErrorDisplay
            error={eulerQuoteError}
            isLoading={isEulerQuoteLoading || isValidatingQuotes}
            onRefetch={handleRefetchQuotes}
            isRefetching={isQuoteRefetching || isValidatingQuotes}
            className="mt-4"
          />
        )}

        {/* Timeout error */}
        <TimeoutErrorDisplay
          isTimedOut={swapTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        {/* Error Display */}
        {displayError && !isTimeoutError(displayError) && !eulerQuoteError && (
          <TransactionErrorPanel
            error={displayError}
            showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
          />
        )}

        <BigBlocksBanner className="mt-4" />

        {/* Action Row */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.exchange} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={fromToken} className="h-6" />} actionStatus={executeStatus}>
              Swap Debt
            </ActionRow.Title>
            <ActionRow.Description successMessage="Debt swapped!" actionStatus={executeStatus} variant="compact" />
            <ActionRow.Action
              onAction={handleExecuteWithTimeout}
              status={executeStatus}
            >
              {swapTimeout.isTimedOut
                ? 'Retry'
                : isSimulating || isValidatingQuotes
                  ? 'Preparing...'
                  : isEulerQuoteLoading
                    ? 'Quoting...'
                    : 'Swap'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
